import React, { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";

const API_BASE =
  process.env.NODE_ENV === "production"
    ? "https://cyberpinnacle-backend.onrender.com"
    : "http://localhost:5000";

export default function Reports() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const res = await axios.get(`${API_BASE}/admin/stats`);
        const { lastForensics } = res.data;
        setReport(lastForensics);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    }
    loadStats();
  }, []);

  if (loading) {
    return <p className="text-center text-green-400">Loading Report...</p>;
  }

  if (!report) {
    return (
      <p className="text-center text-green-400">
        No forensic reports available yet. Run an analysis in Forensics Lab.
      </p>
    );
  }

  const badge =
    report.threatLevel === "CRITICAL"
      ? { icon: "🟥", color: "#f87171", label: "CRITICAL" }
      : report.threatLevel === "WARNING"
      ? { icon: "🟧", color: "#facc15", label: "WARNING" }
      : { icon: "🟩", color: "#4ade80", label: "INFO" };

  // PDF generator function
  const generatePDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(20);
    doc.text("CyberPinnacle Digital Forensics Report", 14, 20);

    doc.setFontSize(12);
    doc.text(`Case Number: CP-${new Date(report.timestamp).getFullYear()}-${report.id}`, 14, 35);
    doc.text(`Date: ${new Date(report.timestamp).toLocaleString()}`, 14, 43);
    doc.text(`Threat Level: ${report.threatLevel}`, 14, 51);
    doc.text(`Analyzed File: ${report.filename}`, 14, 59);
    doc.text(`Size: ${report.size} bytes`, 14, 67);

    doc.setFontSize(14);
    doc.text("-------------------------------------------------------------", 14, 75);
    doc.text("Forensic Summary", 14, 85);

    doc.setFontSize(11);
    doc.text(report.analysis || "No details available.", 14, 95, { maxWidth: 180 });

    doc.setFontSize(12);
    doc.text("-------------------------------------------------------------", 14, 160);
    doc.text("Generated by CyberPinnacle AI — DFIR & SecOps Engine", 14, 170);

    doc.save(`CyberPinnacle_Report_${report.id}.pdf`);
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <header className="mb-6">
        <h1 className="text-3xl font-bold text-green-400 text-center">
          CyberPinnacle Forensic Report
        </h1>
        <p className="text-sm text-green-500 text-center">
          Automatically generated by CyberPinnacle AI
        </p>
      </header>

      <div className="border border-green-700 rounded-xl p-4 bg-black/70 text-sm">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-green-300">Case Report</h2>
          <span
            className="px-3 py-1 rounded-full font-semibold border text-xs flex items-center gap-1"
            style={{ color: badge.color, borderColor: badge.color }}
          >
            <span>{badge.icon}</span> {badge.label}
          </span>
        </div>

        <hr className="border-green-800 my-2" />

        <p>
          <span className="font-semibold text-green-400">Case ID:</span>{" "}
          CP-{new Date(report.timestamp).getFullYear()}-{report.id}
        </p>
        <p>
          <span className="font-semibold text-green-400">Date:</span>{" "}
          {new Date(report.timestamp).toLocaleString()}
        </p>
        <p>
          <span className="font-semibold text-green-400">Filename:</span>{" "}
          {report.filename}
        </p>
        <p>
          <span className="font-semibold text-green-400">Size:</span>{" "}
          {report.size} bytes
        </p>
        <p>
          <span className="font-semibold text-green-400">Detected Level:</span>{" "}
          {report.threatLevel}
        </p>

        <hr className="border-green-800 my-4" />

        <h3 className="font-semibold text-green-300 mb-2">
          Forensic Summary
        </h3>
        <p className="whitespace-pre-wrap text-green-200 text-sm">
          {report.analysis || "No analysis details available"}
        </p>

        <div className="mt-6 flex gap-3">
          <button
            onClick={generatePDF}
            className="px-4 py-2 border border-green-500 bg-green-500 text-black rounded-md text-sm hover:bg-green-400"
          >
            Export PDF
          </button>

          <button
            onClick={() => navigator.clipboard.writeText(report.analysis)}
            className="px-4 py-2 border border-green-500 rounded-md text-sm hover:bg-green-900/40"
          >
            Copy Report
          </button>
        </div>
      </div>
    </div>
  );
}
